// =============================================================================
// features/assistant/blocks/LeadActionButtons.tsx — 4 ações por lead no
// bloco `stage_leads` do copiloto (F30-S04): abrir no CRM, abrir conversa,
// mover de estágio, marcar como perdido.
//
// Gating client-side é defesa em profundidade — o backend também aplica
// authorize() em cada rota (fonte de verdade). Mesmo padrão de
// EscalateToCreditButton.tsx: `if (!can) return null`.
//
// Ação do HUMANO logado (não da IA) — chamada com o JWT/RBAC do usuário, não
// passa pelo painel de reversão de ai-actions (docs/22 + slot F30-S04).
//
// Gaps de contrato registrados no PR (decisões tomadas dentro deste slot,
// sem tocar backend):
//   1. `conversation_id` não vem em StageLeadsResponse (F30-S02/S03) — "Abrir
//      conversa" navega ao inbox geral (/conversas), não a um deep-link de
//      conversa específica. Fast-follow: expor conversation_id no bloco.
//   2. GET /api/kanban/stages não expõe `canonical_role` — a lista de
//      destino do "mover estágio" mostra TODAS as stages da org, sem filtrar
//      pelas etapas do funil de crédito.
//   3. `kanban_card_id` não vem em StageLeadsResponse — buscado sob demanda
//      (GET /api/leads/:id) só quando o menu "mover estágio" abre, evitando
//      N requests para a lista toda.
// =============================================================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import type { LeadResponse } from '../../../hooks/crm/types';
import { LEAD_QUERY_KEY } from '../../../hooks/crm/useLead';
import { LEADS_QUERY_KEY } from '../../../hooks/crm/useLeads';
import { KANBAN_STAGES_KEY, useKanbanStages } from '../../../hooks/kanban/useKanbanStages';
import { ApiError, api } from '../../../lib/api';
import { useAuthStore } from '../../../lib/auth-store';
import { cn } from '../../../lib/cn';

import { MarkAsLostModal } from './MarkAsLostModal';
import type { StageLeadItem } from './types';

interface LeadActionButtonsProps {
  lead: StageLeadItem;
}

const actionButtonClass = cn(
  'inline-flex items-center gap-1.5 rounded-sm border border-border-strong bg-surface-1 px-2.5 py-1.5',
  'font-sans font-semibold text-ink-2 transition-all duration-fast ease-out',
  'hover:border-azul hover:text-azul hover:shadow-e1 hover:-translate-y-px',
  'active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/40',
  'disabled:opacity-50 disabled:pointer-events-none disabled:translate-y-0',
);

const dangerButtonClass = cn(
  'inline-flex items-center gap-1.5 rounded-sm border border-border-strong bg-surface-1 px-2.5 py-1.5',
  'font-sans font-semibold text-danger transition-all duration-fast ease-out',
  'hover:border-danger hover:shadow-e1 hover:-translate-y-px',
  'active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger/40',
);

/** Rota de "Abrir no CRM" — CrmDetailPage (App.tsx `/crm/:id`). Função pura,
 * testável sem montar React (mesma usada no onClick real, sem drift). */
export function resolveCrmPath(leadId: string): string {
  return `/crm/${leadId}`;
}

export const CONVERSATIONS_INBOX_PATH = '/conversas';

/**
 * Rota de "Abrir conversa".
 *
 * Deep-link direto quando o lead tem conversa: a inbox lê `?conversation=<id>`
 * e já abre aquele atendimento. Sem conversa (lead manual ou importado), cai na
 * inbox geral — melhor que link quebrado.
 *
 * Função pura e exportada: o onClick usa exatamente esta, sem duplicar a regra.
 */
export function resolveConversationPath(conversationId: string | null): string {
  return conversationId === null
    ? CONVERSATIONS_INBOX_PATH
    : `${CONVERSATIONS_INBOX_PATH}?conversation=${conversationId}`;
}

export interface LeadActionVisibility {
  canMove: boolean;
  canMarkLost: boolean;
}

/** Gating client-side das 2 ações condicionadas a permissão ("mover estágio"
 * e "marcar como perdido") — defesa em profundidade, o backend também aplica
 * authorize() em cada rota (fonte de verdade, ver header do arquivo). Função
 * pura e EXPORTADA — o componente usa exatamente esta função (não duplica a
 * lógica), para que o teste em LeadListCard.test.tsx exercite as strings de
 * permissão REAIS do gating, não uma réplica reescrita à mão. */
export function getLeadActionVisibility(
  hasPermission: (permission: string) => boolean,
): LeadActionVisibility {
  return {
    canMove: hasPermission('kanban:move'),
    canMarkLost: hasPermission('leads:write'),
  };
}

export function LeadActionButtons({ lead }: LeadActionButtonsProps): React.JSX.Element {
  const navigate = useNavigate();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const { canMove, canMarkLost } = getLeadActionVisibility(hasPermission);
  const [lostModalOpen, setLostModalOpen] = React.useState(false);

  return (
    <div className="flex flex-wrap items-center gap-1.5 pt-1">
      <button
        type="button"
        onClick={() => navigate(resolveCrmPath(lead.lead_id))}
        className={actionButtonClass}
        style={{ fontSize: 'var(--text-xs)', minHeight: 32 }}
      >
        Abrir no CRM
      </button>

      <button
        type="button"
        onClick={() => navigate(resolveConversationPath(lead.conversation_id))}
        className={actionButtonClass}
        style={{ fontSize: 'var(--text-xs)', minHeight: 32 }}
      >
        Abrir conversa
      </button>

      {canMove && <MoveStageAction leadId={lead.lead_id} />}

      {canMarkLost && (
        <button
          type="button"
          onClick={() => setLostModalOpen(true)}
          className={dangerButtonClass}
          style={{ fontSize: 'var(--text-xs)', minHeight: 32 }}
        >
          Marcar como perdido
        </button>
      )}

      {lostModalOpen && (
        <MarkAsLostModal
          leadId={lead.lead_id}
          leadName={lead.name}
          onClose={() => setLostModalOpen(false)}
        />
      )}
    </div>
  );
}

// ── Mover estágio ────────────────────────────────────────────────────────────

/** Queries invalidadas após mover um card de estágio: cards + stages do
 * Kanban (board), detalhe e lista do lead (CRM). Prefixo `['kanban','cards']`
 * casa com qualquer variante de KanbanFilters (TanStack Query v5 invalida por
 * prefixo). Função pura — mesma lista usada no `onSuccess` real, sem drift. */
export function buildMoveStageInvalidationKeys(leadId: string): readonly (readonly unknown[])[] {
  return [['kanban', 'cards'], KANBAN_STAGES_KEY, LEAD_QUERY_KEY(leadId), LEADS_QUERY_KEY({})];
}

function MoveStageAction({ leadId }: { leadId: string }): React.JSX.Element {
  const [open, setOpen] = React.useState(false);
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const { stages } = useKanbanStages();

  // Lazy: só busca o kanban_card_id do lead quando o menu abre — evita N
  // requests desnecessários ao renderizar a lista toda (gap 3 do header).
  const { data: leadDetail, isFetching } = useQuery({
    queryKey: LEAD_QUERY_KEY(leadId),
    queryFn: () => api.get<LeadResponse>(`/api/leads/${leadId}`),
    enabled: open,
    staleTime: 30_000,
  });

  const moveMutation = useMutation({
    mutationFn: (toStageId: string) =>
      api.post(`/api/kanban/cards/${leadDetail?.kanban_card_id}/move`, { toStageId }),
    onSuccess: () => {
      setOpen(false);
      for (const queryKey of buildMoveStageInvalidationKeys(leadId)) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
    onError: (err: unknown) => {
      setErrorMsg(
        err instanceof ApiError && err.status === 422
          ? err.message
          : 'Não foi possível mover o lead agora. Tente novamente.',
      );
    },
  });

  React.useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent): void {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const sortedStages = [...stages].sort((a, b) => a.position - b.position);
  const cardId = leadDetail?.kanban_card_id ?? null;

  return (
    <div ref={menuRef} className="relative inline-flex">
      <button
        type="button"
        onClick={() => {
          setErrorMsg(null);
          setOpen((v) => !v);
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        className={actionButtonClass}
        style={{ fontSize: 'var(--text-xs)', minHeight: 32 }}
      >
        Mover estágio
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-0 top-full mt-1 w-56 rounded-sm border border-border z-10 overflow-hidden"
          style={{ background: 'var(--bg-elev-1)', boxShadow: 'var(--elev-3)' }}
        >
          {isFetching && (
            <p className="px-3 py-2.5 font-sans text-xs text-ink-4">Carregando estágios...</p>
          )}
          {!isFetching && cardId === null && (
            <p className="px-3 py-2.5 font-sans text-xs text-ink-4">
              Este lead não tem card ativo no Kanban.
            </p>
          )}
          {!isFetching &&
            cardId !== null &&
            sortedStages.map((stage) => (
              <button
                key={stage.id}
                type="button"
                role="menuitem"
                disabled={moveMutation.isPending}
                onClick={() => moveMutation.mutate(stage.id)}
                className="flex w-full items-center px-3 py-2.5 font-sans text-xs text-ink-2 text-left hover:text-ink hover:bg-surface-hover transition-colors duration-fast disabled:opacity-40"
              >
                {stage.name}
              </button>
            ))}
          {errorMsg && (
            <p className="px-3 py-2 font-sans text-xs text-danger border-t border-border-subtle">
              {errorMsg}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
